/**
 * data/repos/accountsRepo — repositório da área `accounts` (Req 12, Req 13, DD-004).
 *
 * Resolve a fonte via `data/source.ts`:
 * - área real (`VITE_API_REAL_AREAS` contém `accounts`) → GET /v1/accounts;
 * - caso contrário → mock tipado (`data/mock.ts`).
 *
 * O mapeamento DTO→portal vive aqui. Valores monetários em centavos inteiros (RNF 11).
 * Nenhum dado PII é persitido em localStorage (RNF 6, Req 13.4).
 */

import { apiClient, ApiError } from "../../services/ApiClient.js";
import { resolveSource } from "../source.js";
import type {
  AccountListItem,
  Contact,
  AccountTimelineEvent,
  ContactsAccess,
} from "../../features/accounts/types.js";
import type { Currency, MoneyByCurrency } from "../../shared/money/money.js";
import type { AccountTierOption } from "../../types/domain.js";

// ---------------------------------------------------------------------------
// DTO do backend account-management (GET /v1/accounts/{id})
//
// DTO básico de identidade — usado só pela 360° (`getAccount360`). Os campos
// AGREGADOS de enriquecimento CRM (tierLabel/arr/openOpps/ownerDisplayName/
// recência) NÃO pertencem a este DTO: vazariam para a 360° (epic
// accounts-list-enrichment, decisão 6) — por isso são um DTO dedicado de
// listagem (`AccountListItemApiDto`, abaixo). Os IDs brutos de atribuição
// (`tierId`/`accountOwnerId`) SÃO expostos aqui (STORY-03, TASK-04/08 —
// persistidos direto no agregado `Account`) — necessários para pré-preencher
// o seletor de tier/owner em `EditAccountDialog` (STORY-07, TASK-22).
// ---------------------------------------------------------------------------

interface AccountApiDto {
  readonly id: string;
  readonly tenantId: string;
  readonly buId: string;
  readonly name: string;
  readonly normalizedName: string;
  readonly website?: string | null;
  readonly notes?: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly tierId?: string | null;
  readonly accountOwnerId?: string | null;
}

/**
 * Mapeia o DTO básico (`GET /v1/accounts/{id}`) para o shape de portal
 * `AccountListItem`. Os campos AGREGADOS de enriquecimento CRM (rótulo do
 * tier, ARR, oportunidades abertas, nome do owner, recência) não existem
 * neste DTO — ficam `null` (não fabricamos dado; a 360° não é a superfície de
 * agregação, ver `mapApiAccountListItem` para a listagem). `tierId`/`ownerId`
 * (IDs brutos de atribuição) são propagados quando presentes.
 *
 * `website` e `notes` são mapeados do DTO (nullable → undefined quando ausentes).
 */
function mapApiAccount(dto: AccountApiDto): AccountListItem {
  return {
    id: dto.id,
    name: dto.name,
    buId: dto.buId,
    tierId: dto.tierId ?? null,
    tierLabel: null,
    arr: null,
    openOpps: null,
    ownerId: dto.accountOwnerId ?? null,
    ownerDisplayName: null,
    daysSinceLastActivity: null,
    // Campos editáveis disponíveis no contrato atual.
    ...(dto.website != null ? { website: dto.website } : {}),
    ...(dto.notes != null ? { notes: dto.notes } : {}),
  };
}

// ---------------------------------------------------------------------------
// DTO do backend account-management (GET /v1/accounts) — listagem enriquecida
//
// `AccountListItemResponse` (epic accounts-list-enrichment, STORY-05): DTO
// dedicado de listagem, com os campos de CRM agregados de outros domínios
// (pipeline, activity-management, organization) via S2S REST interno.
// ---------------------------------------------------------------------------

interface AccountListItemApiDto {
  readonly id: string;
  readonly buId: string;
  readonly name: string;
  readonly website?: string | null;
  readonly notes?: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly tierId: string | null;
  readonly tierLabel: string | null;
  /** Mapa moeda→centavos (ADR-0008, sem câmbio) — `null` quando não há ARR. */
  readonly arr: Record<string, number> | null;
  readonly openOpps: number | null;
  readonly ownerId: string | null;
  readonly ownerDisplayName: string | null;
  readonly daysSinceLastActivity: number | null;
  /** Timestamp bruto (ISO 8601, UTC) — activity-derived-signals, LDG-0010. */
  readonly lastActivityDate?: string | null;
  /** activity-derived-signals, LDG-0018. */
  readonly hasNextActivity?: boolean;
}

/**
 * Converte o mapa moeda→centavos do wire (`Record<string, number>`, chaves
 * livres) para `MoneyByCurrency` (chaves restritas a `Currency`). Moedas
 * desconhecidas do portal (fora de BRL/USD/EUR) são ignoradas defensivamente
 * — nunca quebram o parse da listagem por uma moeda ainda não suportada na UI.
 */
function toMoneyByCurrency(raw: Record<string, number> | null): MoneyByCurrency | null {
  if (raw == null) return null;
  const KNOWN_CURRENCIES: readonly Currency[] = ["BRL", "USD", "EUR"];
  const result: MoneyByCurrency = {};
  let hasEntry = false;
  for (const currency of KNOWN_CURRENCIES) {
    const cents = raw[currency];
    if (cents != null) {
      result[currency] = cents;
      hasEntry = true;
    }
  }
  return hasEntry ? result : null;
}

/**
 * Mapeia o DTO de listagem (`AccountListItemResponse`) para `AccountListItem`,
 * propagando os campos de enriquecimento CRM (TASK-17, REQ-02).
 */
function mapApiAccountListItem(dto: AccountListItemApiDto): AccountListItem {
  return {
    id: dto.id,
    name: dto.name,
    buId: dto.buId,
    tierId: dto.tierId,
    tierLabel: dto.tierLabel,
    arr: toMoneyByCurrency(dto.arr),
    openOpps: dto.openOpps,
    ownerId: dto.ownerId,
    ownerDisplayName: dto.ownerDisplayName,
    daysSinceLastActivity: dto.daysSinceLastActivity,
    lastActivityDate: dto.lastActivityDate ?? null,
    hasNextActivity: dto.hasNextActivity ?? false,
    ...(dto.website != null ? { website: dto.website } : {}),
    ...(dto.notes != null ? { notes: dto.notes } : {}),
  };
}

// ---------------------------------------------------------------------------
// DTO do backend para contatos (GET /v1/accounts/{id}/contacts)
//
// email e phone chegam como null quando o contato ainda não possui esses dados,
// quando a política de PII do backend filtra os campos para o papel do usuário
// autenticado, ou quando o contato foi anonimizado (LGPD, isAnonymized=true).
// O portal preserva null para distinguir "dado ausente/filtrado" de "string vazia"
// — importante para a UI não oferecer ações de edição/contato sobre registros LGPD.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// DTO do backend para timeline (GET /v1/accounts/{id}/timeline)
//
// O backend retorna os eventos já ordenados por timestamp decrescente (Req 6.4).
// `type` segue a lista canônica (email | call | note | stage_change | ai);
// `actor` é omitido/null quando o backend não conhece o autor do evento.
//
// ADR-0049 — o corpo passou de array puro para o envelope `{ events, sections }`.
// A ordem de implantação é CLIENTE PRIMEIRO: este arquivo aceita os DOIS formatos
// durante a janela, de modo que um portal novo funcione contra um servidor antigo.
// A função de normalização é dívida com dono e gatilho declarados (LDG-0369): o
// ramo que aceita o array sai quando o servidor tiver devolvido envelope em
// produção por uma janela completa de cache do bundle.
// ---------------------------------------------------------------------------

interface AccountTimelineEventApiDto {
  readonly id: string;
  readonly type: "email" | "call" | "note" | "stage_change" | "ai";
  readonly title: string;
  readonly timestamp: string;
  readonly actor?: string | null;
  /** REQ-04.3/04.9 — sempre presente no servidor novo; ausente no array cru antigo. */
  readonly piiMasked?: boolean;
}

/** Nome canônico de uma seção componível da linha do tempo (ADR-0049). */
export type AccountTimelineSectionName = "contacts" | "activities";

/**
 * Estado de uma seção da linha do tempo.
 *
 * `complete` AFIRMA ausência quando não há eventos; `unavailable` recusa afirmar
 * qualquer coisa. Renderizar o estado vazio para uma seção `unavailable` é o
 * defeito, não a solução — é proibido pela decisão 3 do ADR-0049.
 *
 * `cause` não é exibida ao usuário: vai para log e telemetria do cliente.
 */
export interface AccountTimelineSection {
  readonly name: AccountTimelineSectionName | string;
  readonly status: "complete" | "unavailable";
  readonly cause?: string;
}

interface AccountTimelineEnvelopeApiDto {
  readonly events: readonly AccountTimelineEventApiDto[];
  readonly sections?: readonly AccountTimelineSection[];
}

/**
 * Todas as seções conhecidas marcadas com o mesmo estado.
 *
 * A lista é a ORDEM DE COMPOSIÇÃO do backend, e uma lista vazia nunca é resposta
 * válida: `sections` tem uma entrada por seção componível, sempre — é a presença
 * da entrada que permite distinguir "respondeu e está vazia" de "não respondeu".
 */
function allSections(status: "complete" | "unavailable", cause?: string): AccountTimelineSection[] {
  const names: AccountTimelineSectionName[] = ["contacts", "activities"];
  return names.map((name) => (cause != null ? { name, status, cause } : { name, status }));
}

/**
 * Aceita array cru (servidor antigo) ou envelope (servidor novo).
 *
 * Ausência de `sections` na resposta é tratada como `complete` para todas as
 * seções — degrada exatamente para o comportamento anterior, jamais para erro
 * (ADR-0049, decisão 3).
 */
function normalizeTimelineResponse(
  body: readonly AccountTimelineEventApiDto[] | AccountTimelineEnvelopeApiDto,
): { events: AccountTimelineEvent[]; sections: AccountTimelineSection[] } {
  if (Array.isArray(body)) {
    return { events: body.map(mapApiTimelineEvent), sections: allSections("complete") };
  }

  const envelope = body as AccountTimelineEnvelopeApiDto;
  return {
    events: (envelope.events ?? []).map(mapApiTimelineEvent),
    sections: envelope.sections != null ? [...envelope.sections] : allSections("complete"),
  };
}

function mapApiTimelineEvent(dto: AccountTimelineEventApiDto): AccountTimelineEvent {
  return {
    id: dto.id,
    type: dto.type,
    title: dto.title,
    timestamp: dto.timestamp,
    ...(dto.actor != null ? { actor: dto.actor } : {}),
    piiMasked: dto.piiMasked ?? false,
  };
}

interface ContactApiDto {
  readonly id: string;
  readonly accountId: string;
  readonly name: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly role: string | null;
  readonly isAnonymized: boolean;
  /** REQ-04.9 — sempre presente (booleano, nunca omitido) no contrato real. */
  readonly piiMasked: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
}

function mapApiContact(dto: ContactApiDto): Contact {
  return {
    id: dto.id,
    accountId: dto.accountId,
    // Preserva null quando o backend mascara por PII (piiMasked=true) ou quando
    // o dado é genuinamente ausente/anonimizado. A distinção entre null e ""
    // é importante para a UI não oferecer edição sobre registros restritos.
    name: dto.name,
    email: dto.email,
    phone: dto.phone,
    role: dto.role,
    isAnonymized: dto.isAnonymized,
    piiMasked: dto.piiMasked,
  };
}

// ---------------------------------------------------------------------------
// Tipos de resposta do repo
// ---------------------------------------------------------------------------

export interface AccountsPage {
  readonly items: readonly AccountListItem[];
  readonly totalCount: number;
}

export interface Account360Data {
  readonly account: AccountListItem;
  readonly contacts: readonly Contact[];
  readonly timeline: readonly AccountTimelineEvent[];
  /**
   * Estado de cada seção componível da linha do tempo (ADR-0049).
   *
   * Existe para que a tela possa distinguir "não há eventos" de "não foi possível
   * consultar". **Ainda não é consumido pelos componentes** — o estado visual de
   * seção degradada é a parte do escopo do cliente que continua aberta em
   * LDG-0369, com dono nominal. Expor o dado aqui é o que torna essa parte
   * implementável sem mexer no repositório de dados de novo.
   */
  readonly timelineSections: readonly AccountTimelineSection[];
  /**
   * REQ-04 (portal) — discriminante derivado do status HTTP de `GET …/contacts`,
   * nunca do papel primário local. `granted` (200): exibição por contato decidida
   * pelo `piiMasked` de cada um. `restricted` (403): painel inteiro mostra "Acesso
   * restrito". `unavailable` (qualquer outra falha): estado de indisponível — nunca
   * tratado como lista vazia (afirmação falsa de ausência).
   */
  readonly contactsAccess: ContactsAccess;
}

/**
 * Coluna ordenável da lista de contas (REQ-06).
 * Só `"name"` é ordenável server-side nesta iteração — `tier`/ARR/saúde são
 * campos de enriquecimento CRM sem sort server-side no contrato atual; ARR é
 * ordenável apenas client-side (ver `AccountList.tsx`, TASK-20).
 */
export type AccountSortField = "name";
export type AccountSortDirection = "asc" | "desc";

export interface ListAccountsParams {
  readonly buId?: string | null;
  readonly search?: string;
  /** Id do `AccountTierOption` (TASK-18) — não mais o rótulo/união fixa. */
  readonly tier?: string | null;
  readonly page?: number;
  readonly pageSize?: number;
  readonly sortBy?: AccountSortField;
  readonly sortDirection?: AccountSortDirection;
}

// ---------------------------------------------------------------------------
// Mapeamento mock → shape de portal
// ---------------------------------------------------------------------------

/**
 * Heurística de mock para `daysSinceLastActivity`: o mock legado (`Account`)
 * só tem a classificação de saúde pronta (`good`/`watch`/`risk`), não a
 * recência em si. Converte para um valor plausível dentro de cada faixa de
 * `deriveAccountHealth` (`features/accounts/health.ts`) — preserva o
 * comportamento visual anterior sem inventar um novo campo no mock legado.
 */
function mockDaysSinceLastActivity(health: string): number {
  switch (health) {
    case "good":
      return 3;
    case "watch":
      return 10;
    case "risk":
      return 20;
    default:
      return 3;
  }
}

/** Resolve o id do tier configurável a partir do rótulo do mock legado. */
function resolveTierId(tierLabel: string, tiers: readonly AccountTierOption[]): string | null {
  return tiers.find((t) => t.label === tierLabel)?.id ?? null;
}

function mapMockAccount(
  a: {
    id: string; name: string; segment: string; tier: string;
    health: string; arr: number; openOpps: number; owner: string;
  },
  ownersMap: Record<string, { name: string }>,
  tiers: readonly AccountTierOption[],
): AccountListItem {
  return {
    id: a.id,
    name: a.name,
    segment: a.segment,
    tierId: resolveTierId(a.tier, tiers),
    tierLabel: a.tier,
    // arr do mock é em reais (sem centavos) — convertemos para centavos, moeda BRL.
    arr: { BRL: a.arr * 100 },
    openOpps: a.openOpps,
    ownerId: a.owner,
    ownerDisplayName: ownersMap[a.owner]?.name ?? a.owner,
    daysSinceLastActivity: mockDaysSinceLastActivity(a.health),
  };
}

/** Contatos mock por conta (dados PII de teste). */
const MOCK_CONTACTS: readonly Contact[] = [
  { id: "c1", accountId: "a1", name: "Fernanda Lima",  email: "fernanda.lima@pernambucanas.com", phone: "+55 11 99001-1001", role: "Diretora de TI", isAnonymized: false, piiMasked: false },
  { id: "c2", accountId: "a1", name: "Carlos Pereira", email: "carlos.p@pernambucanas.com",      phone: "+55 11 99001-1002", role: "Gerente de TI",  isAnonymized: false, piiMasked: false },
  { id: "c3", accountId: "a2", name: "João Alves",     email: "joao.alves@leroymerlin.com.br",   phone: "+55 11 99002-2001", role: "CTO",            isAnonymized: false, piiMasked: false },
  { id: "c4", accountId: "a9", name: "Diego Santos",   email: "diego@techpag.com.br",            phone: "+55 11 99009-9001", role: "CEO",            isAnonymized: false, piiMasked: false },
];

/** Timeline mock por conta. */
function mockTimeline(accountId: string): AccountTimelineEvent[] {
  return [
    { id: `${accountId}-ev1`, type: "note",         title: "Reunião de discovery",                  timestamp: "2026-06-10", actor: "Ana Beatriz" },
    { id: `${accountId}-ev2`, type: "stage_change", title: "Oportunidade avançou para Negociação",  timestamp: "2026-06-12", actor: "Sistema" },
    { id: `${accountId}-ev3`, type: "email",        title: "Proposta enviada por e-mail",           timestamp: "2026-06-13", actor: "Ana Beatriz" },
  ];
}

// ---------------------------------------------------------------------------
// createAccount
// ---------------------------------------------------------------------------

/**
 * Dados de entrada para criação de conta (POST /v1/accounts).
 *
 * `buId` deve ser um GUID real do escopo do usuário, obtido dos claims do JWT
 * (ADR-0009). Não use ids provenientes do seletor de BU de UI (esses são mock).
 */
export interface CreateAccountInput {
  readonly name: string;
  readonly website?: string;
  readonly notes?: string;
  /**
   * GUID da Business Unit à qual a conta pertencerá.
   * Obrigatório para usuários multi-BU (o backend retorna ACC-ERR-010 sem ele).
   * Deve vir dos claims do JWT — não do seletor de BU do portal.
   */
  readonly buId?: string;
}

/**
 * Cria uma nova conta via POST /v1/accounts (área "accounts" real) ou
 * retorna um id gerado localmente em modo mock.
 *
 * Em modo mock o id é gerado com Date.now() para ser único na sessão;
 * a conta NÃO é persistida no mock — a lista não refletirá o novo item
 * entre reloads (apenas o id é devolvido para satisfazer o contrato).
 */
export async function createAccount(
  input: CreateAccountInput,
): Promise<{ id: string }> {
  const source = await resolveSource("accounts");

  if (source.kind === "api") {
    // Body camelCase conforme contrato POST /v1/accounts
    const body: Record<string, unknown> = { name: input.name };
    if (input.website) body["website"] = input.website;
    if (input.notes) body["notes"] = input.notes;
    if (input.buId) body["buId"] = input.buId;

    // POST /v1/accounts retorna 201 com corpo vazio + header Location; o id da
    // nova conta é extraído do Location por apiClient.create (ADR-0009).
    return apiClient.create("/accounts", body);
  }

  // Mock: gera id local — não persiste entre reloads (apenas satisfaz o contrato).
  const id = `mock-${Date.now()}`;
  return { id };
}

// ---------------------------------------------------------------------------
// listAccounts
// ---------------------------------------------------------------------------

export async function listAccounts(params: ListAccountsParams = {}): Promise<AccountsPage> {
  const source = await resolveSource("accounts");

  if (source.kind === "api") {
    const query: Record<string, string | number> = {};
    if (params.search) query["search"] = params.search;
    // Id do AccountTierOption (TASK-18) — o controller filtra pela FK do tier.
    if (params.tier) query["tier"] = params.tier;
    if (params.page) query["page"] = params.page;
    // O controller espera `pageSize` (camelCase via [FromQuery]) — não `page_size`.
    if (params.pageSize) query["pageSize"] = params.pageSize;
    // AccountSortField/SortDirection do backend fazem model binding case-insensitive
    // (ASP.NET Core) — envia o valor lowercase do enum do portal direto.
    if (params.sortBy) query["sortBy"] = params.sortBy;
    if (params.sortDirection) query["sortDirection"] = params.sortDirection;

    const dto = await apiClient.get<{ items?: AccountListItemApiDto[]; totalCount?: number }>(
      "/accounts",
      Object.keys(query).length > 0 ? query : undefined,
    );
    // Mapeamento tipado: AccountListItemApiDto → AccountListItem (com enriquecimento CRM, TASK-17).
    const items = (dto.items ?? []).map(mapApiAccountListItem);
    return { items, totalCount: dto.totalCount ?? items.length };
  }

  // Mock
  if (!source.mock) throw new Error(`[repo] mock não disponível para área: ${source.area}`);
  const mock = source.mock;
  const ownersMap = Object.fromEntries(
    Object.entries(mock.owners).map(([k, v]) => [k, { name: v.name }])
  );

  let items = mock.accounts.map((a) => mapMockAccount(a, ownersMap, mock.accountTiers));

  // Filtragem local (mock)
  if (params.search) {
    const q = params.search.toLowerCase();
    items = items.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        (a.segment?.toLowerCase().includes(q) ?? false),
    );
  }
  if (params.tier) {
    items = items.filter((a) => a.tierId === params.tier);
  }

  // Ordenação local (mock) — mesmo contrato do backend (REQ-06).
  if (params.sortBy === "name") {
    const dir = params.sortDirection === "desc" ? -1 : 1;
    items = [...items].sort((a, b) => dir * a.name.localeCompare(b.name));
  }

  const totalCount = items.length;

  // Paginação local (mock) — mesmo contrato do backend (REQ-05): totalCount
  // reflete o conjunto filtrado inteiro, items só a página pedida.
  if (params.page && params.pageSize) {
    const start = (params.page - 1) * params.pageSize;
    items = items.slice(start, start + params.pageSize);
  }

  return { items, totalCount };
}

// ---------------------------------------------------------------------------
// getAccount360
// ---------------------------------------------------------------------------

export async function getAccount360(accountId: string): Promise<Account360Data> {
  const source = await resolveSource("accounts");

  if (source.kind === "api") {
    // GET /v1/accounts/{id} — retorna AccountApiDto (contrato atual do backend).
    const dto = await apiClient.get<AccountApiDto>(`/accounts/${accountId}`);
    const account = mapApiAccount(dto);

    // Os contatos são buscados via endpoint dedicado. O status HTTP é o único
    // discriminante de acesso (REQ-04, portal) — nunca o papel primário local:
    // 200 → granted (exibição por contato decidida pelo piiMasked de cada um);
    // 403 (account.view_pii=none para o papel) → restricted (painel inteiro
    // mostra "Acesso restrito", nunca "Nenhum contato cadastrado"); qualquer
    // outra falha → unavailable (nunca tratada como lista vazia silenciosa —
    // seria afirmação falsa de ausência, o que o ADR-0049 proíbe).
    let contacts: Contact[] = [];
    let contactsAccess: ContactsAccess = "granted";
    try {
      const dtos = await apiClient.get<ContactApiDto[]>(`/accounts/${accountId}/contacts`);
      contacts = dtos.map(mapApiContact);
    } catch (err) {
      contactsAccess = err instanceof ApiError && err.status === 403 ? "restricted" : "unavailable";
    }

    // A timeline é buscada via GET /accounts/{id}/timeline (endpoint dedicado, disponível).
    // O try/catch continua existindo — a 360° não deve falhar por causa da timeline —, mas
    // DEIXOU DE ENGOLIR o sinal: uma falha da chamada inteira (inclusive um 503 explícito)
    // marca todas as seções como indisponíveis, em vez de devolver lista vazia. Sem isso, o
    // discriminante que o backend passou a produzir morreria aqui, e a decisão inteira do
    // ADR-0049 não produziria efeito visível (decisão 3, segundo colapso). 403 (REQ-04,
    // account.view_pii=none) marca a causa como "forbidden" em vez de "transport" — a causa
    // não é exibida (vai para telemetria), mas distingue negação de falha de rede.
    let timeline: AccountTimelineEvent[] = [];
    let timelineSections: AccountTimelineSection[] = allSections("complete");
    try {
      const body = await apiClient.get<
        readonly AccountTimelineEventApiDto[] | AccountTimelineEnvelopeApiDto
      >(`/accounts/${accountId}/timeline`);
      const normalized = normalizeTimelineResponse(body);
      timeline = normalized.events;
      timelineSections = normalized.sections;
    } catch (err) {
      timeline = [];
      const cause = err instanceof ApiError && err.status === 403 ? "forbidden" : "transport";
      timelineSections = allSections("unavailable", cause);
    }

    return { account, contacts, timeline, timelineSections, contactsAccess };
  }

  // Mock
  if (!source.mock) throw new Error(`[repo] mock não disponível para área: ${source.area}`);
  const mock = source.mock;
  const ownersMap = Object.fromEntries(
    Object.entries(mock.owners).map(([k, v]) => [k, { name: v.name }])
  );
  const raw = mock.accounts.find((a) => a.id === accountId);
  if (!raw) throw new Error(`WEB-ERR-011: conta ${accountId} não encontrada`);

  const account = mapMockAccount(raw, ownersMap, mock.accountTiers);
  const contacts = MOCK_CONTACTS.filter((c) => c.accountId === accountId);
  const timeline = mockTimeline(accountId);

  // No mock não existe produtor para ficar indisponível: todas as seções são completas
  // e o acesso a contatos é sempre concedido (sem política de PII simulada).
  return {
    account,
    contacts,
    timeline,
    timelineSections: allSections("complete"),
    contactsAccess: "granted",
  };
}

// ---------------------------------------------------------------------------
// updateAccount — PATCH /v1/accounts/{id}
// ---------------------------------------------------------------------------

/** Campos editáveis de uma conta (todos opcionais — envia apenas o que mudou). */
export interface UpdateAccountInput {
  readonly name?: string;
  readonly website?: string;
  readonly notes?: string;
}

/**
 * Atualiza os dados básicos de uma conta via PATCH /v1/accounts/{id}.
 * Retorna void — o backend responde 204 No Content em caso de sucesso.
 *
 * Em modo mock a operação é um no-op (o mock não é persistido entre renders).
 */
export async function updateAccount(
  accountId: string,
  input: UpdateAccountInput,
): Promise<void> {
  const source = await resolveSource("accounts");

  if (source.kind === "api") {
    // Envia apenas os campos definidos (PATCH parcial).
    const body: Record<string, unknown> = {};
    if (input.name !== undefined) body["name"] = input.name;
    if (input.website !== undefined) body["website"] = input.website;
    if (input.notes !== undefined) body["notes"] = input.notes;
    await apiClient.patch<void>(`/accounts/${accountId}`, body);
    return;
  }
  // Mock: no-op.
}

// ---------------------------------------------------------------------------
// assignAccountEnrichment — PUT /v1/accounts/{id}/enrichment (STORY-03, STORY-07 TASK-22)
// ---------------------------------------------------------------------------

/**
 * Atribuição de tier/owner de uma conta. Semântica PUT — substitui
 * integralmente o estado de enriquecimento: `null` remove a atribuição
 * correspondente (REQ-03), `undefined` mantém o valor atual do backend
 * (campo omitido do payload).
 */
export interface AssignAccountEnrichmentInput {
  readonly tierId?: string | null;
  readonly ownerId?: string | null;
}

/**
 * Atribui (ou remove) o tier e o owner de uma conta existente via
 * `PUT /v1/accounts/{id}/enrichment`. Retorna void — 204 No Content.
 *
 * Em modo mock a operação é um no-op (o mock não é persistido entre renders).
 */
export async function assignAccountEnrichment(
  accountId: string,
  input: AssignAccountEnrichmentInput,
): Promise<void> {
  const source = await resolveSource("accounts");

  if (source.kind === "api") {
    await apiClient.put<void>(`/accounts/${accountId}/enrichment`, {
      tierId: input.tierId ?? null,
      accountOwnerId: input.ownerId ?? null,
    });
    return;
  }
  // Mock: no-op.
}

// ---------------------------------------------------------------------------
// listContacts — GET /v1/accounts/{id}/contacts
// ---------------------------------------------------------------------------

/**
 * Lista os contatos de uma conta via GET /v1/accounts/{id}/contacts.
 *
 * O backend devolve nome/email/phone/role como `null` e `piiMasked: true` quando o alcance de PII do usuário não cobre a conta (REQ-04); o mapeamento preserva o `null` e o `piiMasked`, sem colapsar para string vazia. Um 403 propaga como `ApiError` — quem precisa distinguir acesso restrito de lista vazia é `getAccount360`, que devolve `contactsAccess`.
 */
export async function listContacts(accountId: string): Promise<Contact[]> {
  const source = await resolveSource("accounts");

  if (source.kind === "api") {
    const dtos = await apiClient.get<ContactApiDto[]>(`/accounts/${accountId}/contacts`);
    return dtos.map(mapApiContact);
  }

  // Mock: filtra pelo accountId.
  return MOCK_CONTACTS.filter((c) => c.accountId === accountId) as Contact[];
}

// ---------------------------------------------------------------------------
// createContact — POST /v1/accounts/{id}/contacts
// ---------------------------------------------------------------------------

/** Dados obrigatórios e opcionais para criação de contato (Req 13.3). */
export interface CreateContactInput {
  readonly name: string;
  readonly email: string;
  readonly phone?: string;
  readonly role?: string;
}

/**
 * Cria um contato para a conta via POST /v1/accounts/{id}/contacts.
 * O backend responde 201 No Content (sem id no corpo nem header Location).
 * Invalide a query de contatos após a chamada para recarregar a lista.
 *
 * Em modo mock a operação é um no-op.
 */
export async function createContact(
  accountId: string,
  input: CreateContactInput,
): Promise<void> {
  const source = await resolveSource("accounts");

  if (source.kind === "api") {
    const body: Record<string, unknown> = {
      name: input.name,
      email: input.email,
    };
    if (input.phone) body["phone"] = input.phone;
    if (input.role) body["role"] = input.role;
    await apiClient.post<void>(`/accounts/${accountId}/contacts`, body);
    return;
  }
  // Mock: no-op.
}

// ---------------------------------------------------------------------------
// updateContact — PATCH /v1/accounts/{accountId}/contacts/{id}
// ---------------------------------------------------------------------------

/** Campos editáveis de um contato (todos opcionais — PATCH parcial). */
export interface UpdateContactInput {
  readonly name?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly role?: string;
}

/**
 * Atualiza os dados de um contato via PATCH /v1/accounts/{accountId}/contacts/{id}.
 * Retorna void — o backend responde 204 No Content em caso de sucesso.
 *
 * Em modo mock a operação é um no-op.
 */
export async function updateContact(
  accountId: string,
  contactId: string,
  input: UpdateContactInput,
): Promise<void> {
  const source = await resolveSource("accounts");

  if (source.kind === "api") {
    const body: Record<string, unknown> = {};
    if (input.name !== undefined) body["name"] = input.name;
    if (input.email !== undefined) body["email"] = input.email;
    if (input.phone !== undefined) body["phone"] = input.phone;
    if (input.role !== undefined) body["role"] = input.role;
    await apiClient.patch<void>(`/accounts/${accountId}/contacts/${contactId}`, body);
    return;
  }
  // Mock: no-op.
}

// ---------------------------------------------------------------------------
// deleteContact — DELETE /v1/accounts/{accountId}/contacts/{id}
// ---------------------------------------------------------------------------

/**
 * Anonimiza (LGPD) um contato via DELETE /v1/accounts/{accountId}/contacts/{id}.
 * Requer papel `tenant_admin` — o backend retorna 403 para papéis insuficientes.
 * Retorna void — o backend responde 204 No Content em caso de sucesso.
 *
 * Em modo mock a operação é um no-op.
 */
export async function deleteContact(
  accountId: string,
  contactId: string,
): Promise<void> {
  const source = await resolveSource("accounts");

  if (source.kind === "api") {
    await apiClient.delete<void>(`/accounts/${accountId}/contacts/${contactId}`);
    return;
  }
  // Mock: no-op.
}

// ---------------------------------------------------------------------------
// deleteAccount — DELETE /v1/accounts/{id}
// ---------------------------------------------------------------------------

/**
 * Exclui DEFINITIVAMENTE uma conta e seus contatos via DELETE /v1/accounts/{id}.
 * Requer papel `tenant_admin` — o backend responde 403 para qualquer outro papel,
 * e nesse caso SEM corpo (a policy do controller curto-circuita antes do middleware
 * de erro, então não há `code` para ler).
 *
 * Retorna void — o backend responde 204 No Content em caso de sucesso.
 *
 * Erros esperados, todos propagados como `ApiError` para o chamador traduzir:
 * - 404 `ACC-ERR-003` — conta inexistente, de outro tenant ou de BU fora do escopo.
 * - 409 `ACC-ERR-017` — a conta tem oportunidades vinculadas (abertas, ganhas ou perdidas).
 * - 503 `ACC-ERR-018` — não foi possível verificar as oportunidades; a exclusão foi bloqueada.
 *
 * Em modo mock a operação é um no-op.
 */
export async function deleteAccount(accountId: string): Promise<void> {
  const source = await resolveSource("accounts");

  if (source.kind === "api") {
    await apiClient.delete<void>(`/accounts/${accountId}`);
    return;
  }
  // Mock: no-op.
}
